/**
 * One visit, as a customer reads it — `docs/proposal.md` §6.1–§6.4, drawn to
 * boards 3, 4, 5 and 12 (`design/mockups/43-customer-proposal/html/`).
 *
 * Four questions, in the order somebody asks them: **how many**, **when**,
 * **what does it cost**, and **what is actually in it**. The card answers the
 * first three in its head and the band, and opens as far as the quote's
 * detail setting says for the fourth.
 *
 * **The card is the whole card**, border and all, because an optional visit
 * *is* a different card rather than the same card inside something: dashed
 * and white until it is switched on, tinted once it is (board 4's three
 * states). The switch sits in the head beside what it adds — "+$640 · Add
 * this visit" — and nothing about the layout moves when it flips. The first
 * version put a checkbox in front of the whole card and the card inside a
 * label, which pushed every line of it 28px right and doubled the padding;
 * nothing on the boards draws that.
 *
 * **The count badge is the headline** because "9 visits" is the thing a
 * customer remembers and the thing a competitor's quote will be compared on.
 * A project badges **DAYS** rather than visits, so a 3-day patio is never
 * read as three trips; an open-ended rental badges **∞**.
 *
 * **No date leaves this component** that §6.2 did not let out: the band says
 * a frequency and, at most, a season in months. Real dates draw only when the
 * server sent them, which needs the company's permission *and* this visit's
 * own mode — both decided before the payload was built.
 */
import { useState, type ReactNode } from 'react'
import {
  repriced, selectionTotals, timingDetail, timingHeadline, visitIncluded, withoutChildren,
  withoutServiceLines,
  type ProposalKind, type ProposalLine, type ProposalSelection,
  type ProposalService, type ProposalView, type ProposalVisit,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDollarsExact } from '../../lib/format.js'
import { Switch } from '../../components/primitives.js'
import { Glyph } from '../../frame/mark-glyphs.js'
import {
  GlyphCheck, GlyphChevronDown, GlyphChevronUp, GlyphLock, GlyphLoop,
  KindEquipment, KindFee, KindLabor, KindMaterial,
} from './glyphs.js'
import { ProposalTag } from './tag.js'
import { VisitPrice, serviceShowsPrice } from './price.js'

export interface VisitCardProps {
  view: ProposalView
  visit: ProposalVisit
  selection: ProposalSelection
  onSelection: (selection: ProposalSelection) => void
  /**
   * The number in the circle: the visit's place on the whole proposal —
   * included visits first, one number per question, then the optional ones —
   * so board 1's renovation is "Visit 3" and the winterization is "4".
   */
  index: number | null
  phone: boolean
  /**
   * Open the first service's lines on load. Board 1 opens one, so the depth
   * is visible without a click; opening every service on every card would
   * make the page a bill of materials.
   */
  openFirst?: boolean
  /** Inside an option card the option is the card, so this draws bare. */
  bare?: boolean
}

export function VisitCard(props: VisitCardProps) {
  const { view, visit, selection, onSelection, index, phone, bare } = props
  const optional = visit.kind === 'optional'
  /*
   * **Only an optional visit is drawn quiet**, and only while it is off: the
   * card goes dashed and white, the badge and the band go grey, and the
   * price is muted (§6.1). An *option* in a choose-one group is not that —
   * nothing is preselected there, and greying every option made the one
   * screen whose job is a comparison look switched off.
   */
  const on = !optional || visitIncluded(visit, selection)

  /*
   * The band's price with whatever the customer has ticked on this visit in
   * it. What was ticked is the difference `selectionTotals` — the page's one
   * sum — makes with and without the ticked services and lines, so the band,
   * the rail and the total card move by the same amount; the server's own
   * figure stays the starting point, rather than being re-derived here.
   */
  const added = on ? addedBySelection(view, selection, visit.id) : 0
  const priced = added === 0
    ? visit
    : { ...visit, price: repriced(visit.price, visit.price.total + added) }

  const toggle = () => onSelection(on
    /*
     * Switching a visit off takes its optional services and lines with it
     * (§7.2) — nothing stays ticked under a parent nobody is buying.
     * Switching it back on brings the required work and leaves the extras
     * off, so nothing is ever added by accident.
     */
    ? withoutChildren(view, selection, visit.id)
    : { ...selection, visits: [...selection.visits, visit.id] })

  const body = (
    <>
      <div className={cn('flex items-start justify-between', phone ? 'gap-3' : 'gap-4')}>
        <div className={cn('flex min-w-0 flex-1', phone ? 'gap-3' : 'gap-3.5')}>
          {index !== null && (
            <span className={cn('flex shrink-0 items-center justify-center rounded-full font-bold',
              phone ? 'h-[30px] w-[30px] text-[14px]' : 'h-[34px] w-[34px] text-[15px]',
              on ? 'bg-brand text-brand-on'
                : 'border-[1.6px] border-line-strong bg-surface text-text-muted')}>
              {index}
            </span>
          )}
          <div className="flex min-w-0 flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2.5">
              <h3 className={cn('m-0 font-bold leading-[1.2] tracking-[-0.02em] text-text',
                phone ? 'text-[20px]' : 'text-[23px]')}>
                {visit.name}
              </h3>
              {visit.kind === 'included' && <ProposalTag kind="included" />}
              {optional && <ProposalTag kind={on ? 'added' : 'optional'} />}
            </div>
            {visit.description && (
              <p className={cn('m-0 max-w-[600px] leading-[1.55] text-text-body',
                phone ? 'text-[14.5px]' : 'text-[15.5px]')}>
                {visit.description}
              </p>
            )}
          </div>
        </div>

        {/* The optional visit's switch, beside what it adds (board 4). */}
        {optional && !phone && (
          <div className="flex shrink-0 items-center gap-3">
            <span className="flex flex-col items-end leading-[1.2]">
              <b className="tnum text-[18px] font-bold text-text">
                {`+${formatDollarsExact(visit.price.total)}`}
              </b>
              <span className={cn('text-[12.5px] font-semibold',
                on ? 'text-brand-tx' : 'text-text-muted')}>
                {on ? 'Added' : 'Add this visit'}
              </span>
            </span>
            <Switch look="proposal" checked={on} onChange={toggle}
              label={`Add ${visit.name}`} hideLabel />
          </div>
        )}
      </div>

      {/*
        * On a phone the switch is its own 44px row under the head (§14 item
        * 7): the row is the target, so a thumb never has to find a 46px pill
        * at the edge of a 390px screen.
        */}
      {optional && phone && (
        <OptionalRow variant="visit" on={on} onToggle={toggle} label={`Add ${visit.name}`}
          figure={`+${formatDollarsExact(visit.price.total)}`}
          sub={on ? 'Added' : 'Add this visit'} />
      )}

      <TimingBand visit={priced} phone={props.phone} on={on} />
      <Services {...props} on={on} />
    </>
  )

  if (bare) return <div className="flex flex-col">{body}</div>

  return (
    <article className={cn('flex flex-col rounded-[18px]',
      phone ? 'px-4 pb-1 pt-[18px]' : 'px-[30px] pb-2 pt-7',
      !optional && 'border border-line bg-surface shadow-proposal',
      optional && !on && 'border-[1.6px] border-dashed border-line-strong bg-surface',
      optional && on && 'border-[1.6px] border-brand-ring bg-brand-t1 shadow-proposal')}>
      {body}
    </article>
  )
}

/**
 * The 44px row a phone uses for a switch: the figure and its word at the
 * left, the 52 × 32 switch at the right, the whole row the target.
 */
function OptionalRow({ variant = 'service', on, onToggle, label, figure, unit, sub, locked }: {
  /**
   * Board 8: a visit's row sits under a hairline with the switch at the left
   * and what it adds at the right; a service's is a tinted well the other way
   * round. Two shapes because they sit in different places, one component
   * because they are one control.
   */
  variant?: 'visit' | 'service'
  on: boolean
  onToggle: () => void
  label: string
  figure: string
  unit?: string | null
  sub: string
  locked?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={locked}
      onClick={onToggle}
      className={cn('flex min-h-[44px] w-full items-center justify-between gap-3 text-left',
        variant === 'visit'
          ? 'mt-3.5 flex-row-reverse border-t border-line-soft pt-3.5'
          : 'mt-3 rounded-[12px] bg-surface-alt px-3 py-2.5')}
    >
      <span className={cn('flex flex-col gap-px', variant === 'visit' ? 'items-end' : 'items-start')}>
        <span className={cn('tnum text-[15px] font-bold', on ? 'text-text' : 'text-text-muted')}>
          {figure}
          {unit && <span className="text-[12.5px] font-medium text-text-muted">{` / ${unit}`}</span>}
        </span>
        <span className={cn('tnum text-[12.5px]', on ? 'text-brand-tx' : 'text-text-faint')}>
          {sub}
        </span>
      </span>
      <Switch look="proposal-touch" checked={on} disabled={locked} readOnly
        onChange={() => undefined} label={label} hideLabel />
    </button>
  )
}

// ---------------------------------------------------------------------------
// The band: how many, when, what it costs
// ---------------------------------------------------------------------------

function TimingBand({ visit, phone, on }: { visit: ProposalVisit; phone: boolean; on: boolean }) {
  const season = visit.timing.season
  const optional = visit.kind === 'optional'
  return (
    <div className={cn('rounded-2xl border',
      phone ? 'mt-3.5 p-3.5' : 'mt-[18px] px-5 py-[18px]',
      on ? 'border-brand-ring bg-brand-t1' : 'border-line-soft bg-surface-alt')}>
      <div className={cn('flex items-center', phone ? 'gap-3' : 'gap-[18px]')}>
        <CountBadge visit={visit} on={on} phone={phone} />

        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <b className={cn('inline-flex items-center gap-2 font-[650]',
            phone ? 'text-[16px]' : 'text-[17.5px]')}>
            {visit.timing.frequency && (
              <GlyphLoop size={phone ? 16 : 17} className="shrink-0 text-brand-tx" />
            )}
            {timingHeadline(visit)}
          </b>
          {timingDetail(visit) && (
            <span className={cn('leading-[1.45] text-text-body',
              phone ? 'text-[13.5px]' : 'text-[14.5px]')}>
              {timingDetail(visit)}
            </span>
          )}
        </div>

        {!phone && (
          <VisitPrice visit={visit} layout="band" muted={!on} plus={optional} />
        )}
      </div>

      {/* On a phone the price is its own row under a rule (board 8). */}
      {phone && (
        <VisitPrice visit={visit} layout="phone" muted={!on} plus={optional} />
      )}

      {/*
        * The months bar, and only where the season has one: past eight months
        * `seasonLabel` sends an empty list and the label stands alone.
        *
        * One joined bar of equal cells (boards 3 and 8), and it **wraps rather
        * than scrolling**: seven months at the phone's width would need a
        * strip wider than the card, and a strip that scrolls puts everything
        * after June off the edge under a headline reading "April to late
        * October" — half a span is not a smaller span but a wrong one. So on
        * a phone past four months the cells become a grid of tiles instead.
        */}
      {visit.timing.mode === 'season' && season && season.months.length > 0 && (
        <MonthsBar months={season.months} on={on} phone={phone} />
      )}

      {/*
        * Real dates, which arrive only when a company allowed them and an
        * estimator picked them for this visit. **There is no "show all"**:
        * past twelve the tiles become one block per month — a dot per visit
        * and a count, which is the shape of the commitment without the list.
        */}
      {visit.timing.dates.length > 0 && (
        <DateTiles dates={visit.timing.dates} on={on} phone={phone} />
      )}

      {/* A rental's one-time charge, kept apart from the weekly figure. */}
      {visit.oneTime && (
        <div className={cn('mt-3.5 flex items-center justify-between border-t border-brand-ln pt-3',
          'text-text-body', phone ? 'text-[13.5px]' : 'text-[14.5px]')}>
          <span>{visit.oneTime.label}</span>
          <b className="tnum text-[15.5px] text-text">{formatDollarsExact(visit.oneTime.amount)}</b>
        </div>
      )}
    </div>
  )
}

/**
 * The timing band alone, for Settings › Proposal's "How a visit reads now"
 * (board 15) — the **same** band the page draws, at the phone's size, so a
 * company changing a default sees the component a customer will, rather than
 * a description of it that could drift.
 */
export function TimingPreview({ visit }: { visit: ProposalVisit }) {
  return <TimingBand visit={visit} phone on />
}

function MonthsBar({ months, on, phone }: { months: string[]; on: boolean; phone: boolean }) {
  const columns = phone ? Math.min(months.length, 4) : months.length
  const rows = Math.ceil(months.length / columns)
  return (
    <div
      className={cn('grid overflow-hidden rounded-[11px] border',
        phone ? 'mt-3' : 'mt-3.5', on ? 'border-brand-ln' : 'border-line')}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {months.map((month, at) => (
        <span
          key={`${month}-${at}`}
          className={cn('flex min-w-0 items-center justify-center font-bold tracking-[0.02em]',
            phone ? 'h-8 text-[12px]' : 'h-9 text-[12.5px]',
            at % columns !== 0 && 'border-l',
            Math.floor(at / columns) < rows - 1 && 'border-b',
            on ? 'border-brand-ln bg-brand-t2 text-brand-tx' : 'border-line bg-line-soft text-text-muted')}
        >
          {month}
        </span>
      ))}
    </div>
  )
}

/**
 * The number, and the word for what it counts.
 *
 * 66px on a desktop and 56px on a phone (boards 3 and 8). An optional visit
 * nobody has switched on draws it grey — filled, never outlined, because the
 * badge is the one thing on the card that must read at a glance either way.
 */
function CountBadge({ visit, on, phone }: {
  visit: ProposalVisit
  on: boolean
  phone: boolean
}) {
  return (
    <span
      className={cn('flex shrink-0 flex-col items-center justify-center gap-px leading-none',
        phone ? 'h-14 w-14 rounded-[15px]' : 'h-[66px] w-[66px] rounded-[17px]',
        on ? 'bg-brand text-brand-on' : 'bg-line text-text-body')}
    >
      <b className={cn('tnum font-bold tracking-[-0.02em]', phone ? 'text-[26px]' : 'text-[31px]')}>
        {visit.count.openEnded ? '∞' : visit.count.n ?? 1}
      </b>
      <span className={cn('font-bold uppercase tracking-[0.1em] opacity-[0.92]',
        phone ? 'text-[9px]' : 'text-[10px]')}>
        {countWord(visit)}
      </span>
    </span>
  )
}

/** VISITS · VISIT · DAYS · DAY · WEEKS — §6.1's five, and no others. */
function countWord(visit: ProposalVisit): string {
  if (visit.count.openEnded) return 'weeks'
  return visit.count.unit
}

/**
 * Board 3 row 3: white tiles on the band, the month in the brand's text shade
 * over the day. Four to a row on a desktop at four dates, five on a phone
 * (§14 item 5); past twelve, one block per month.
 */
function DateTiles({ dates, on, phone }: { dates: string[]; on: boolean; phone: boolean }) {
  if (dates.length <= 12) {
    const columns = phone ? 5 : Math.min(dates.length, dates.length <= 4 ? 4 : 9)
    return (
      <div className="mt-3.5 grid gap-2"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {dates.map((day, at) => (
          <span
            key={`${day}-${at}`}
            className={cn('flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-[11px]',
              'border bg-surface', dates.length <= 4 && !phone ? 'h-[58px]' : 'h-[46px]',
              on ? 'border-brand-ln' : 'border-line')}
          >
            <span className={cn('text-[10.5px] font-bold uppercase leading-none tracking-[0.08em]',
              on ? 'text-brand-tx' : 'text-text-muted')}>
              {monthOf(day)}
            </span>
            <b className={cn('tnum leading-none text-text',
              dates.length <= 4 && !phone ? 'text-[19px]' : 'text-[16px]')}>
              {dayOf(day)}
            </b>
          </span>
        ))}
      </div>
    )
  }

  /* Past twelve: one block per month, a dot per visit, and the count. */
  const months = new Map<string, number>()
  for (const day of dates) months.set(monthOf(day), (months.get(monthOf(day)) ?? 0) + 1)
  return (
    <div className="mt-3.5 flex flex-wrap gap-2">
      {[...months].map(([month, count]) => (
        <span key={month}
          className={cn('flex flex-col gap-1.5 rounded-[11px] border bg-surface px-3 py-2',
            on ? 'border-brand-ln' : 'border-line')}>
          <span className={cn('text-[10.5px] font-bold uppercase tracking-[0.08em]',
            on ? 'text-brand-tx' : 'text-text-muted')}>
            {month}
          </span>
          <span className="flex gap-1">
            {Array.from({ length: count }, (_unused, at) => (
              <span key={at}
                className={cn('h-1.5 w-1.5 rounded-full', on ? 'bg-brand' : 'bg-line-strong')} />
            ))}
          </span>
          <span className="tnum text-[11.5px] font-semibold text-text-body">
            {count === 1 ? '1 visit' : `${count} visits`}
          </span>
        </span>
      ))}
    </div>
  )
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const monthOf = (day: string): string => MONTHS[Number(day.slice(5, 7)) - 1] ?? ''
const dayOf = (day: string): string => String(Number(day.slice(8, 10)))

// ---------------------------------------------------------------------------
// What is in it (§6.4)
// ---------------------------------------------------------------------------

function Services(props: VisitCardProps & { on: boolean }) {
  const { visit, phone, openFirst } = props
  if (visit.services.length === 0) {
    return <div className={phone ? 'h-4' : 'h-[22px]'} />
  }

  /*
   * At **Visits** only the choices are left (§6.4: detail never hides a
   * choice), under "Add to each visit" where the extra repeats and "You can
   * add" where there is one visit — board 5 cell 6 and board 12.
   */
  const shown = visit.detail === 'visits'
    ? visit.services.filter((one) => one.optional)
    : visit.services
  if (shown.length === 0) {
    return <div className={phone ? 'h-4' : 'h-[22px]'} />
  }
  const many = (visit.count.n ?? 1) > 1 || visit.count.openEnded
  const heading = visit.detail === 'visits'
    ? (many ? 'Add to each visit' : 'You can add')
    : many ? 'What each visit includes' : 'What is included'

  /* The first service that has lines to show opens on load, where asked. */
  const firstWithLines = visit.detail === 'lines'
    ? shown.find((one) => one.lines.length > 0)?.id
    : undefined

  return (
    <div>
      <div className={cn('text-[11.5px] font-bold uppercase tracking-[0.09em] text-text-faint',
        phone ? 'mt-4' : 'mt-5')}>
        {heading}
      </div>
      {shown.map((service, at) => (
        <ServiceRow
          key={service.id}
          {...props}
          service={service}
          first={at === 0}
          startOpen={openFirst === true && service.id === firstWithLines}
        />
      ))}
    </div>
  )
}

function ServiceRow({
  view, visit, service, selection, onSelection, on, phone, first, startOpen,
}: VisitCardProps & {
  service: ProposalService
  on: boolean
  first: boolean
  startOpen: boolean
}) {
  const [open, setOpen] = useState(startOpen)
  const ticked = !service.optional || selection.services.includes(service.id)
  /* An optional service under a visit nobody has added is shown and locked. */
  const locked = service.optional && !on

  /*
   * A service with no listed lines has nothing to open onto: every line of an
   * unlisted kind is *in the price* and out of the JSON (§6.4), so a toggle
   * here would be a control that reveals an empty box.
   */
  const canOpen = visit.detail === 'lines' && service.lines.length > 0
  const showsPrice = serviceShowsPrice(visit)
  const toggle = () => onSelection(ticked
    /*
     * Switching a service off takes its optional lines with it (§7.2), and
     * switching it back on brings the required work and leaves the extras
     * off.
     */
    ? withoutServiceLines(view, selection, service.id)
    : { ...selection, services: [...selection.services, service.id] })

  const adds = addsOf(service, visit)

  return (
    <div className={cn(!first && 'border-t border-line-soft')}>
      <div className={cn('flex items-start', phone ? 'gap-3 pb-3 pt-3.5' : 'gap-3.5 pb-3.5 pt-4')}>
        <span
          className={cn('flex shrink-0 items-center justify-center rounded-[11px]',
            phone ? 'h-[34px] w-[34px]' : 'h-[38px] w-[38px]')}
          style={{
            /*
             * The **division's** colour, which is the one thing on this page
             * that does not follow the brand (§5.3): a division tile means a
             * kind of work, and that meaning has to survive whatever colour
             * the company picked. `color-mix` over `rgb(var(…))` because the
             * design tokens are bare triples and a triple is not a colour.
             */
            backgroundColor: service.division.colour
              ? `color-mix(in srgb, ${service.division.colour} 14%, rgb(var(--color-surface)))`
              : 'rgb(var(--color-surface-alt))',
            color: service.division.colour ?? 'rgb(var(--color-text-muted))',
          }}
        >
          <Glyph set="division" slug={service.division.icon} size={phone ? 17 : 19} />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="flex flex-wrap items-center gap-2">
            <b className={cn('font-[650] tracking-[-0.005em]',
              phone ? 'text-[15.5px]' : 'text-[16.5px]',
              locked ? 'text-text-faint' : 'text-text')}>
              {service.name}
            </b>
            {service.optional && <ProposalTag kind={ticked && !locked ? 'added' : 'optional'} />}
          </span>
          {service.description && (
            <span className={cn('max-w-[560px] leading-[1.5] text-text-muted',
              phone ? 'text-[14px]' : 'text-[14.5px]')}>
              {service.description}
            </span>
          )}
          {service.measure && (
            <span className="tnum text-[13px] text-text-faint">{service.measure}</span>
          )}
          {locked && (
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-text-faint">
              <GlyphLock size={12} />
              Add the visit first to choose this
            </span>
          )}
          {canOpen && (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              className="mt-1 inline-flex w-fit items-center gap-1 text-[13.5px] font-semibold
                         text-brand-tx"
            >
              {open ? 'Hide what is included' : 'What is included'}
              {open ? <GlyphChevronUp size={15} /> : <GlyphChevronDown size={15} />}
            </button>
          )}
        </div>

        {/* The figure, and on a desktop the switch beside it. */}
        {service.optional
          ? !phone && (
            <div className="flex shrink-0 items-start gap-3.5">
              <span className="flex flex-col items-end gap-px text-right">
                <span className={cn('tnum text-[16px] font-bold',
                  ticked && !locked ? 'text-text' : 'text-text-muted')}>
                  {adds.figure}
                  {adds.unit && (
                    <span className="text-[13px] font-medium text-text-muted">
                      {` ${adds.unit}`}
                    </span>
                  )}
                </span>
                <span className="tnum text-[12.5px] text-text-faint">{adds.sub}</span>
              </span>
              <Switch look="proposal" checked={ticked && !locked} disabled={locked}
                label={`Add ${service.name}`} hideLabel onChange={toggle} />
            </div>
          )
          : showsPrice && (
            <span className="tnum shrink-0 text-[16px] font-bold text-text">
              {formatDollarsExact(service.priceEach)}
            </span>
          )}
      </div>

      {service.optional && phone && (
        <div className="-mt-1 mb-3">
          <OptionalRow on={ticked && !locked} locked={locked} onToggle={toggle}
            label={`Add ${service.name}`} figure={adds.figure}
            unit={adds.unit?.replace(/^\/\s*/, '') ?? null} sub={adds.sub} />
        </div>
      )}

      {/*
        * The lines, when open — and **an optional line always draws**,
        * whatever the detail setting and whether or not the service is open
        * (§6.4): a customer can only take what they can see.
        */}
      <Lines
        view={view}
        visit={visit}
        service={service}
        selection={selection}
        onSelection={onSelection}
        open={open && canOpen}
        locked={locked || !ticked || !on}
        phone={phone}
      />
    </div>
  )
}

/**
 * What an optional extra adds, in the unit of the thing it joins (§6.3,
 * board 12): "+$42 / month · for 3 months · $14 per visit" on a visit billed
 * monthly, "+$14 per visit · $126 for all 9" where the visit is priced by the
 * trip. The server worked out the period share, so the two cannot disagree
 * about which denominator was used.
 */
function addsOf(service: ProposalService, visit: ProposalVisit): {
  figure: string; unit: string | null; sub: string
} {
  const n = visit.count.n ?? 1
  const each = formatDollarsExact(service.priceEach)
  const plan = visit.price.plan
  if (service.addsPerPeriod !== null && plan.unit && visit.price.view !== 'visit') {
    const periods = plan.periods
    return {
      figure: `+${formatDollarsExact(service.addsPerPeriod)}`,
      unit: `/ ${plan.every > 1 ? `${plan.every} ${plan.unit}s` : plan.unit}`,
      sub: [
        periods ? `for ${periods} ${periods === 1 ? plan.unit : `${plan.unit}s`}` : null,
        n > 1 ? `${each} per visit` : null,
      ].filter(Boolean).join(' · '),
    }
  }
  if (n > 1) {
    return {
      figure: `+${each}`,
      unit: 'per visit',
      sub: `${formatDollarsExact(service.priceTotal)} for all ${n}`,
    }
  }
  return { figure: `+${each}`, unit: null, sub: 'added to your total' }
}

/** Materials, Labor, Equipment, Fees — in that order, each with its glyph. */
const KIND_WORDS: Record<ProposalKind, string> = {
  material: 'Materials', labor: 'Labor', equipment: 'Equipment', fee: 'Fees',
}
const KIND_GLYPHS: Record<ProposalKind, (props: { size?: number }) => ReactNode> = {
  material: KindMaterial, labor: KindLabor, equipment: KindEquipment, fee: KindFee,
}
const KIND_ORDER: ProposalKind[] = ['material', 'labor', 'equipment', 'fee']

/**
 * Board 5: a grid of kind label and lines, a dashed rule above each kind,
 * indented under the service's name on a desktop and full width on a phone.
 * Line prices only where the company itemizes (`linePrices`), and the hours
 * only where it quotes them (`showHours`) — board 5 cells 3 and 4.
 */
function Lines({ view, visit, service, selection, onSelection, open, locked, phone }: {
  view: ProposalView
  visit: ProposalVisit
  service: ProposalService
  selection: ProposalSelection
  onSelection: (selection: ProposalSelection) => void
  open: boolean
  locked: boolean
  phone: boolean
}) {
  const lines = open ? service.lines : service.lines.filter((one) => one.optional)
  if (lines.length === 0) return null
  const itemized = view.settings.linePrices && serviceShowsPrice(visit)

  return (
    <div className={phone ? 'pb-2.5' : 'pb-3.5 pl-[52px]'}>
      {KIND_ORDER.map((kind) => {
        const of = lines.filter((line) => line.kind === kind)
        /* A kind group with no lines is not drawn (§6.4). */
        if (of.length === 0) return null
        const KindGlyph = KIND_GLYPHS[kind]
        return (
          <div key={kind}
            className={cn('flex border-t border-dashed border-line',
              phone ? 'gap-3 py-2.5' : 'gap-[18px] py-[11px]')}>
            <span className={cn('inline-flex shrink-0 items-start gap-[7px] pt-[5px]',
              'text-[11.5px] font-bold uppercase tracking-[0.07em] text-text-faint',
              phone ? 'w-24' : 'w-[108px]')}>
              <KindGlyph size={14} />
              {KIND_WORDS[kind]}
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              {of.map((line) => (
                <LineRow
                  key={line.id}
                  line={line}
                  showHours={visit.showHours}
                  itemized={itemized}
                  phone={phone}
                  ticked={selection.lines.includes(line.id)}
                  locked={locked}
                  onTick={(next) => onSelection({
                    ...selection,
                    lines: next
                      ? [...selection.lines, line.id]
                      : selection.lines.filter((id) => id !== line.id),
                  })}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function LineRow({ line, showHours, itemized, phone, ticked, locked, onTick }: {
  line: ProposalLine
  showHours: boolean
  itemized: boolean
  phone: boolean
  ticked: boolean
  locked: boolean
  onTick: (ticked: boolean) => void
}) {
  /*
   * A labor line arrives with no `qty` and no `unit` (§4.3): its quantity is
   * its crew hours, and they are said once, here, as "4 hours" — only when
   * the quote shows them. Printing the quantity as well is what put "4 h"
   * on every labor line with the switch off and "4 hours · 4 h" with it on.
   */
  const facts = [
    line.hours !== null && showHours ? hoursWords(line.hours) : null,
    line.qty !== null && line.unit ? `${line.qty} ${line.unit}` : null,
  ].filter(Boolean).join(' · ')

  const words = (
    <span className="min-w-0">
      {line.name}
      {facts && <span className="text-text-faint">{` · ${facts}`}</span>}
    </span>
  )

  if (!line.optional) {
    return (
      <div className={cn('flex items-center gap-2.5',
        phone ? 'min-h-8 text-[14px]' : 'min-h-7 text-[14.5px]')}>
        {words}
        {itemized && line.amountEach > 0 && (
          <span className="tnum ml-auto shrink-0 text-[14px] font-semibold text-text">
            {formatDollarsExact(line.amountEach)}
          </span>
        )}
      </div>
    )
  }

  /*
   * An optional line is a tick box, not a switch (board 4): the switch is
   * for a whole visit or service, and a box beside one line inside a list of
   * lines reads as "and this one too", which is what it is.
   */
  return (
    <label className={cn('flex cursor-pointer items-center gap-2.5',
      phone ? 'min-h-[44px] text-[14px]' : 'min-h-7 text-[14.5px]',
      locked ? 'cursor-default text-text-faint' : 'text-text')}>
      <input
        type="checkbox"
        className="peer sr-only"
        checked={ticked && !locked}
        disabled={locked}
        onChange={() => onTick(!ticked)}
      />
      <span className={cn('flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[7px]',
        'border-[1.6px] peer-focus-visible:ring-2 peer-focus-visible:ring-brand-ring',
        locked ? 'border-line bg-surface-alt'
          : ticked ? 'border-brand-ui bg-brand-ui text-brand-on' : 'border-text-faint bg-surface')}>
        {ticked && !locked && <GlyphCheck size={14} />}
      </span>
      {words}
      <span className={cn('tnum ml-auto shrink-0 text-[14px] font-semibold',
        ticked && !locked ? 'text-text' : 'text-text-muted')}>
        {`+${formatDollarsExact(line.amountEach)}`}
      </span>
    </label>
  )
}

/** "4 hours", "1.5 hours", "1 hour" — board 5's words, not the app's "4.0 h". */
function hoursWords(hours: number): string {
  const said = Number.isInteger(hours) ? String(hours) : String(Math.round(hours * 10) / 10)
  return `${said} ${hours === 1 ? 'hour' : 'hours'}`
}

/** What the ticked optional services and lines add to one visit, in all. */
function addedBySelection(
  view: ProposalView, selection: ProposalSelection, visitId: string,
): number {
  if (selection.services.length === 0 && selection.lines.length === 0) return 0
  const withTicks = selectionTotals(view, selection).byVisit[visitId] ?? 0
  const without = selectionTotals(view, { ...selection, services: [], lines: [] })
    .byVisit[visitId] ?? 0
  return Math.round((withTicks - without) * 100) / 100
}
